import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "./index.css";

type Operator = "+" | "-" | "×" | "÷" | null;

export function App() {
	const [display, setDisplay] = useState("0");
	const [previousValue, setPreviousValue] = useState<number | null>(null);
	const [operator, setOperator] = useState<Operator>(null);
	const [waitingForOperand, setWaitingForOperand] = useState(false);

	const inputDigit = useCallback(
		(digit: string) => {
			if (waitingForOperand) {
				setDisplay(digit);
				setWaitingForOperand(false);
			} else {
				setDisplay(display === "0" ? digit : display + digit);
			}
		},
		[display, waitingForOperand],
	);

	const inputDecimal = useCallback(() => {
		if (waitingForOperand) {
			setDisplay("0.");
			setWaitingForOperand(false);
			return;
		}
		if (!display.includes(".")) {
			setDisplay(display + ".");
		}
	}, [display, waitingForOperand]);

	const clear = useCallback(() => {
		setDisplay("0");
		setPreviousValue(null);
		setOperator(null);
		setWaitingForOperand(false);
	}, []);

	const toggleSign = useCallback(() => {
		const value = parseFloat(display);
		if (value !== 0) {
			setDisplay(String(-value));
		}
	}, [display]);

	const inputPercent = useCallback(() => {
		const value = parseFloat(display);
		setDisplay(String(value / 100));
	}, [display]);

	const calculate = useCallback(
		(next: number) => {
			if (previousValue === null || operator === null) return next;
			switch (operator) {
				case "+":
					return previousValue + next;
				case "-":
					return previousValue - next;
				case "×":
					return previousValue * next;
				case "÷":
					return next === 0 ? NaN : previousValue / next;
				default:
					return next;
			}
		},
		[previousValue, operator],
	);

	const handleOperator = useCallback(
		(nextOperator: Operator) => {
			const current = parseFloat(display);

			if (previousValue !== null && !waitingForOperand) {
				const result = calculate(current);
				const resultStr = isNaN(result) ? "Error" : String(result);
				setDisplay(resultStr);
				setPreviousValue(isNaN(result) ? null : result);
			} else {
				setPreviousValue(current);
			}

			setOperator(nextOperator);
			setWaitingForOperand(true);
		},
		[display, previousValue, waitingForOperand, calculate],
	);

	const handleEquals = useCallback(() => {
		if (previousValue === null || operator === null) return;
		const current = parseFloat(display);
		const result = calculate(current);
		const resultStr = isNaN(result) ? "Error" : String(result);
		setDisplay(resultStr);
		setPreviousValue(null);
		setOperator(null);
		setWaitingForOperand(true);
	}, [display, previousValue, operator, calculate]);

	const formatDisplay = (value: string) => {
		if (value === "Error") return value;
		if (value.length > 12) {
			const num = parseFloat(value);
			if (Math.abs(num) >= 1e12) return num.toExponential(5);
			return value.slice(0, 12);
		}
		return value;
	};

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key >= "0" && e.key <= "9") inputDigit(e.key);
			else if (e.key === ".") inputDecimal();
			else if (e.key === "+") handleOperator("+");
			else if (e.key === "-") handleOperator("-");
			else if (e.key === "*") handleOperator("×");
			else if (e.key === "/") { e.preventDefault(); handleOperator("÷"); }
			else if (e.key === "Enter" || e.key === "=") handleEquals();
			else if (e.key === "Escape" || e.key === "c" || e.key === "C") clear();
			else if (e.key === "Backspace") {
				if (display.length > 1) setDisplay(display.slice(0, -1));
				else setDisplay("0");
			}
			else if (e.key === "%") inputPercent();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [inputDigit, inputDecimal, handleOperator, handleEquals, clear, inputPercent, display]);

	const displayFontSize =
		display.length > 10
			? "text-3xl"
			: display.length > 7
				? "text-4xl"
				: "text-5xl";

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<Card className="w-full max-w-xs bg-card/80 backdrop-blur-sm border-muted shadow-2xl">
				<CardContent className="p-4">
					{/* Display */}
					<div className="bg-muted/50 rounded-lg px-4 py-5 mb-4 text-right overflow-hidden">
						{operator && previousValue !== null && (
							<div className="text-xs text-muted-foreground mb-1 h-4">
								{previousValue} {operator}
							</div>
						)}
						<div
							className={`${displayFontSize} font-light tracking-tight text-foreground transition-all truncate`}
						>
							{formatDisplay(display)}
						</div>
					</div>

					{/* Buttons */}
					<div className="grid grid-cols-4 gap-2">
						{/* Row 1 */}
						<CalcButton
							label="AC"
							onClick={clear}
							variant="secondary"
						/>
						<CalcButton
							label="+/−"
							onClick={toggleSign}
							variant="secondary"
						/>
						<CalcButton
							label="%"
							onClick={inputPercent}
							variant="secondary"
						/>
						<CalcButton
							label="÷"
							onClick={() => handleOperator("÷")}
							variant="operator"
							active={operator === "÷" && waitingForOperand}
						/>

						{/* Row 2 */}
						<CalcButton label="7" onClick={() => inputDigit("7")} />
						<CalcButton label="8" onClick={() => inputDigit("8")} />
						<CalcButton label="9" onClick={() => inputDigit("9")} />
						<CalcButton
							label="×"
							onClick={() => handleOperator("×")}
							variant="operator"
							active={operator === "×" && waitingForOperand}
						/>

						{/* Row 3 */}
						<CalcButton label="4" onClick={() => inputDigit("4")} />
						<CalcButton label="5" onClick={() => inputDigit("5")} />
						<CalcButton label="6" onClick={() => inputDigit("6")} />
						<CalcButton
							label="−"
							onClick={() => handleOperator("-")}
							variant="operator"
							active={operator === "-" && waitingForOperand}
						/>

						{/* Row 4 */}
						<CalcButton label="1" onClick={() => inputDigit("1")} />
						<CalcButton label="2" onClick={() => inputDigit("2")} />
						<CalcButton label="3" onClick={() => inputDigit("3")} />
						<CalcButton
							label="+"
							onClick={() => handleOperator("+")}
							variant="operator"
							active={operator === "+" && waitingForOperand}
						/>

						{/* Row 5 */}
						<CalcButton
							label="0"
							onClick={() => inputDigit("0")}
							className="col-span-2"
						/>
						<CalcButton label="." onClick={inputDecimal} />
						<CalcButton
							label="="
							onClick={handleEquals}
							variant="operator"
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function CalcButton({
	label,
	onClick,
	variant = "digit",
	active = false,
	className = "",
}: {
	label: string;
	onClick: () => void;
	variant?: "digit" | "secondary" | "operator";
	active?: boolean;
	className?: string;
}) {
	const baseClasses = "h-14 text-lg font-medium rounded-xl transition-all";

	const variantClasses = {
		digit: "bg-muted hover:bg-muted/70 text-foreground",
		secondary: "bg-muted/50 hover:bg-muted/40 text-muted-foreground",
		operator: active
			? "bg-foreground text-background hover:bg-foreground/90"
			: "bg-primary hover:bg-primary/80 text-primary-foreground",
	};

	return (
		<Button
			variant="ghost"
			className={`${baseClasses} ${variantClasses[variant]} ${className}`}
			onClick={onClick}
		>
			{label}
		</Button>
	);
}

export default App;
